import BulkImport, {
	BulkCallbackResult,
	BulkImportDataTypes,
	BulkImportOption,
	BulkImportOptionStatus,
	BulkImportType,
	BulkType,
	csvTableData,
	IBulkImportTable,
	SearchResult,
	ValueData
} from "./BulkImport";

import { minValue, numeric, required } from "vuelidate/lib/validators";

import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import Vselect3 from "vselect3";
import store from "@/store";

import { Response } from "@/store/responses/types";
import { DeviceDetails } from "@/store/devices/types";

import { getGroup, isValidGroup } from "./CommonBulkValidation";

import BulkImportTable from "@/components/bulk-import/BulkImportTable.vue";
import { downloadCsv } from "./CommonBulkImport";
import { debounce, uniqBy } from "lodash";
import { AreaNode } from "@/types/groups/AreaNode";
import api from "@/services/api.service";
import { getDebouncePeriod } from "@/utils/debounce-helper";

export const BulkTypeData: BulkType<RawAlarmBulkType, AlarmBulkType> = {
    title: "Alarm Automations",
    bulkImportType: BulkImportType.AlarmActions,
    bulkImportCsvTemplate: () => downloadCsv(
        "AlarmActionsBulkImport",
        ["Site Name", "Alarm", "Camera", "Number of Seconds", "PreAlarm"]
    ),
    rawDataTemplate: {
        groupId: 0,
        responseId: 0,
        deviceId: 0,
        numberOfSeconds: 0,
        preAlarm: true,
    },
    bulkTemplate: {
        siteName: "",
        alarm: "",
        camera: "",
        numberOfSeconds: 0,
        preAlarm: false,
    }
}

export const testMethod = (result: BulkCallbackResult<AlarmBulkType, SearchResult<string>, BulkImportTable&IBulkImportTable>) => getGroup(result.currentRow.siteName);

export const searchDeviceDebounce = debounce(async (result: BulkCallbackResult<AlarmBulkType, SearchResult<string>, BulkImportTable&IBulkImportTable>) => {
    const area = getGroup(result.currentRow.siteName);
    await store.dispatch("devices/fetchDevicesByTypeAndAppend", { groupId: area?.groupId, filter: result.eventData.data, deviceTypeId: 1 }, { root: true });

    result.vm.updateProp("options", "options");
    result.eventData.loading(false);
}, getDebouncePeriod());

export const searchAlarmDebounce = debounce(async (result: BulkCallbackResult<AlarmBulkType, SearchResult<string>, BulkImportTable&IBulkImportTable>) => {
    const area = getGroup(result.currentRow.siteName);
    await api.responsesByArea({ GroupID: area?.groupId, Query: result.eventData.data });

    result.vm.updateProp("options", "options");
    result.eventData.loading(false);
}, getDebouncePeriod());

const mergeOptions = function(oldOptions: any) {
    const oldDeviceIDs = oldOptions.map(x => x.deviceID);
    const newOptions = this.newDevices.filter(x => !oldDeviceIDs.includes(x.deviceID));

    return oldOptions.concat(newOptions);
}

export const MapValues = (csvData: csvTableData<AlarmBulkType>[]) => {
    const responses: Response[] = store.state["responses"]?.responses;
    const devices: DeviceDetails[] = store.state["devices"].devices;

    return csvData.map(csvData => {
        const group = getGroup(csvData.siteName);
        const response = responses.find(p => p.title == csvData.alarm && p.groupID == group?.groupId);
        const device =  devices.find(p => p.title == csvData.camera && p.groupID == group?.groupId);

        return ({
            groupId: group?.groupId ?? MetaData.find(
                x => x.valueProperty == "groupId").defaultValue ?? 0,
            responseId: response?.responseID ?? MetaData.find(
                x => x.valueProperty == "responseId").defaultValue ?? 0,
            deviceId: device?.deviceID ?? MetaData.find(
                x => x.valueProperty == "deviceId").defaultValue ?? 0,
            numberOfSeconds: csvData.numberOfSeconds ?? 0,
            preAlarm: csvData.preAlarm ?? true,
            id: csvData.id,
        });
    });
}

/**
 * @summary meta data which includes
 */
export const MetaData: BulkImport[] = [
    {
        displayProperty: "siteName",
        valueProperty: "groupId",
        header: "Area",
        dataType: BulkImportDataTypes.Text,
        invalidTooltip: "Invalid Area Name",
        rowClass: "w-300",
        validation: {
            required,
            isValidGroup: isValidGroup
        },
        focusedComponent: {
            component: AreaTreeSelect,
            props: {
                appendToBody: true,
                reduce: group => group.id,
                value: null
            },
            events: {
                input: (
                    result: BulkCallbackResult<RawAlarmBulkType, AreaNode, BulkImportTable>): Promise<BulkImportOption<[string, number]>> => {
                    return Promise.resolve({ data: [result?.eventData?.label ?? "", result?.eventData?.id ?? 0], status: BulkImportOptionStatus.Singular });
                }
            },
            hasVModel: false,
        },
        mounted: async (csvData: csvTableData<AlarmBulkType>[], valueData: ValueData[]) => {
            await store.dispatch("areas/fetchAreaDictionary", null, { root: true });
        },
        resolveMultiple: false,
        defaultValue: null
    },
    {
        displayProperty: "alarm",
        valueProperty: "responseId",
        header: "Alarm",
        dataType: BulkImportDataTypes.Text,
        invalidTooltip: "Invalid Alarm Name",
        validation:  {
            required,
            isValidResponse: function (responseTitle: any, payload: csvTableData<AlarmBulkType>): boolean {
               // this instance is binding by the component for our validation callbacks.
                const vm: any = this!;
                const result: RawAlarmBulkType = vm.getRawDataById(payload.id);

                // in our rawData, check if our responseId contains an valid ID.
                if (result && !result.responseId) {
                    return false;
                }

                const group = getGroup(payload.siteName);
                const responses: Response[] = store.state["responses"]?.responses ?? [];

                return responses.find(x => x.title == responseTitle && x.groupID == group?.groupId) ? true  : false;
            }
        },
        focusedComponent: {
            component: Vselect3,
            props: {
                appendToBody: true,
                options: (async (result: BulkCallbackResult<RawAlarmBulkType, undefined, BulkImportTable>) => {
                    const responses = (store.state["responses"]?.responses as Response[])?.filter(
                        x => x.groupID == result.currentRow.groupId) ?? [];

                    return uniqBy(responses, x => x.responseID);
                }),
                label: "title"
            },
            events: {
                input: (async (result: BulkCallbackResult<RawAlarmBulkType, Response, BulkImportTable>): Promise<BulkImportOption<[string, number]>>  => {
                    await store.dispatch("devices/fetchDevicesByTypeAndAppendNoFilter", { groupId: result.eventData.groupID, params: { page: 1, pageSize: 25 } }, { root: true });
                    return Promise.resolve({ data: [result?.eventData?.title ?? "", result?.eventData?.responseID ?? 0], status: BulkImportOptionStatus.Singular });
                }),
                search: async (
                    result: BulkCallbackResult<AlarmBulkType, SearchResult<string>, BulkImportTable&IBulkImportTable>
                ): Promise<BulkImportOption<[string, number]>> => {
                    if (!result.eventData.data) {
                        return { data: null, status: BulkImportOptionStatus.Continue };
                    }

                    result.eventData.loading(true);

                    await searchAlarmDebounce(result);

                    return { data: null, status: BulkImportOptionStatus.Continue };
                }
            },
            hasVModel: false
        },
        mounted: async (csvData: csvTableData<AlarmBulkType>[], valueData: ValueData[]) => {
            const responseTitles = csvData.map(x => x.alarm);
            const responseTitleSet = new Set(responseTitles);
            const uniqueResponseTtitles = Array.from(responseTitleSet);
            await store.dispatch("responses/getResponsesByTitle", uniqueResponseTtitles, { root: true });
        },
        defaultValue: null,
    },
    {
        displayProperty: "camera",
        valueProperty: "deviceId",
        header: "Camera",
        invalidTooltip: "Invalid Camera Name",
        dataType: BulkImportDataTypes.Text,
        validation: {
            required,
            isValidDevice: function (deviceTitle: string, payload: csvTableData<AlarmBulkType>): boolean {
               // this instance is binding by the component for our validation callbacks.
               const vm: any = this!;
               const result: RawAlarmBulkType = vm.getRawDataById(payload.id);

               // in our rawData, check if our responseId contains an valid ID.
               if (result && !result.deviceId) {
                   return false;
               }

                const group = getGroup(payload.siteName);
                const devices: DeviceDetails[] = store.state["devices"]?.devices ?? [];

                return devices.find(x => x.title == deviceTitle && x.groupID == group?.groupId) ? true : false;
            }
        },
        focusedComponent: {
            component: Vselect3,
            props: {
                appendToBody: true,
                options: (async (result: BulkCallbackResult<RawAlarmBulkType, undefined, BulkImportTable>) => {
                    const devices: DeviceDetails[] = (store.state["devices"].devices as DeviceDetails[])?.filter(
                        x => x.groupID == result.currentRow.groupId);
                    return uniqBy(devices, x => x.deviceID);
                }),
                label: "title"
            },
            computed: {
                options: {
                    get: async (result: BulkCallbackResult<RawAlarmBulkType, undefined, BulkImportTable>) => {
                        let oldOptions = (store.state["devices"].devices as DeviceDetails[])?.filter(x => x.groupID == result.currentRow.groupId);
                        oldOptions = uniqBy(oldOptions, x => x.deviceID);

                        const newOptions = mergeOptions(oldOptions);

                        return newOptions(result);
                    }
                }
            },
            events: {
                input: (
                    result: BulkCallbackResult<RawAlarmBulkType, DeviceDetails, BulkImportTable>
                ): Promise<BulkImportOption<[string, number]>>  => {
                    return Promise.resolve({ data: [result?.eventData?.title ?? "", result?.eventData?.deviceID ?? 0], status: BulkImportOptionStatus.Singular });
                },
                search: async (
                    result: BulkCallbackResult<AlarmBulkType, SearchResult<string>, BulkImportTable&IBulkImportTable>
                ): Promise<BulkImportOption<[string, number]>> => {
                    if (!result.eventData.data) return { data: null, status: BulkImportOptionStatus.Continue };
                    result.eventData.loading(true);

                    await searchDeviceDebounce(result);

                    return { data: null, status: BulkImportOptionStatus.Continue };
                }
            },
            hasVModel: false
        },
        mounted: async (csvData: csvTableData<AlarmBulkType>[], valueData: ValueData[]) => {
            const deviceTitles = csvData.map(x => x.camera);
            const deviceTitleSet = new Set(deviceTitles);
            const uniqueDeviceTtitles = Array.from(deviceTitleSet);

            await store.dispatch("devices/getDevicesByTitle", uniqueDeviceTtitles, { root: true });
        },
        defaultValue: null,
    },
    {
        displayProperty: "numberOfSeconds",
        valueProperty: "numberOfSeconds",
        header: "Number of Seconds",
        invalidTooltip: "Invalid number of seconds",
        dataType: BulkImportDataTypes.Number,
        validation: {
                required,
                numeric,
                minValue: minValue(0),
        },
        defaultValue: 0,
    },
    {
        displayProperty: "preAlarm",
        valueProperty: "preAlarm",
        header: "Record pre-alarm footage",
        invalidTooltip: "Invalid pre-alarm footage value",
        dataType: BulkImportDataTypes.Boolean,
        validation: {},
        defaultValue: true,
        thClass: "text-center"
    }
];

export interface AlarmBulkType {
    siteName: string;
    alarm: string;
    camera: string;
    numberOfSeconds: number;
    preAlarm: boolean;
}

export interface RawAlarmBulkType {
    groupId: number;
    responseId: number;
    deviceId: number;
    numberOfSeconds: number;
    preAlarm: boolean;
}
